import React, { useState } from "react";
import { Wand2, ImageIcon, CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { getEnhancedPrompt, generateImage } from "../utils/apiHelpers";
import ImageCard from "./ImageCard";

const STEPS = ["Input", "Enhance", "Approve", "Generate"];

export default function WorkflowText() {
  const [userPrompt, setUserPrompt] = useState("");
  const [enhancedPrompt, setEnhancedPrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [error, setError] = useState("");

  const handleEnhance = async () => {
    if (!userPrompt.trim()) {
      setError("Please enter a prompt first.");
      return;
    }
    setError("");
    setIsLoading(true);
    setLoadingMsg("Engineering your prompt...");
    try {
      const result = await getEnhancedPrompt(userPrompt);
      setEnhancedPrompt(result);
      setCurrentStep(1);
    } catch (err) {
      setError(`Enhancement failed: ${err.message}`);
    } finally {
      setIsLoading(false);
      setLoadingMsg("");
    }
  };

  const handleGenerate = async () => {
    if (!enhancedPrompt.trim()) {
      setError("Enhanced prompt is empty.");
      return;
    }
    setError("");
    setIsLoading(true);
    setLoadingMsg("Generating your image (this takes ~15s)...");
    try {
      const imageUrl = await generateImage(enhancedPrompt);
      setGeneratedImage(imageUrl);
      setCurrentStep(2);
    } catch (err) {
      setError(`Image generation failed: ${err.message}`);
    } finally {
      setIsLoading(false);
      setLoadingMsg("");
    }
  };

  const handleReset = () => {
    setUserPrompt("");
    setEnhancedPrompt("");
    setGeneratedImage(null);
    setCurrentStep(0);
    setError("");
  };

  return (
    <div className="workflow-container">
      {/* Step Tracker */}
      <div className="step-tracker">
        {STEPS.map((label, i) => (
          <div key={i} className={`step-item ${i <= currentStep ? "done" : ""} ${i === currentStep + 1 && !isLoading ? "next" : ""}`}>
            <div className="step-circle">
              {i < currentStep ? <CheckCircle size={14} /> : <span>{i + 1}</span>}
            </div>
            <span className="step-label">{label}</span>
            {i < STEPS.length - 1 && <div className="step-line" />}
          </div>
        ))}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-bar">
          <Loader2 size={18} className="spin" />
          <span>{loadingMsg}</span>
        </div>
      )}

      {/* Step 1: User Input */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-number">01</span>
          <div>
            <h3 className="panel-title">Your Idea</h3>
            <p className="panel-sub">Describe what you want to create — any level of detail</p>
          </div>
        </div>
        <textarea
          className="input-area"
          rows={4}
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          placeholder="e.g. a cat sitting on the moon at sunset..."
          disabled={isLoading}
        />
        <button
          className="btn btn-primary"
          onClick={handleEnhance}
          disabled={isLoading || !userPrompt.trim()}
        >
          <Wand2 size={16} />
          Enhance with AI
        </button>
      </div>

      {/* Step 2: Enhanced Prompt Approval */}
      {currentStep >= 1 && (
        <div className="panel panel-glow">
          <div className="panel-header">
            <span className="panel-number">02</span>
            <div>
              <h3 className="panel-title">AI-Enhanced Prompt</h3>
              <p className="panel-sub">Review, edit if needed, then generate your image</p>
            </div>
          </div>
          <textarea
            className="input-area enhanced"
            rows={5}
            value={enhancedPrompt}
            onChange={(e) => setEnhancedPrompt(e.target.value)}
            disabled={isLoading}
          />
          <div className="btn-row">
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={isLoading || !enhancedPrompt.trim()}
            >
              <ImageIcon size={16} />
              Generate Image
            </button>
            <button className="btn btn-ghost" onClick={handleReset}>
              Start Over
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Generated Image */}
      {currentStep >= 2 && generatedImage && (
        <div className="panel panel-result">
          <div className="panel-header">
            <span className="panel-number">03</span>
            <div>
              <h3 className="panel-title">Your Image</h3>
              <p className="panel-sub">AI-generated from your approved prompt</p>
            </div>
          </div>
          <ImageCard
            imageUrl={generatedImage}
            title="Creative Studio Output"
            prompt={enhancedPrompt}
          />
          <button className="btn btn-ghost" onClick={handleReset} style={{ marginTop: "1rem" }}>
            Create Another
          </button>
        </div>
      )}
    </div>
  );
}
